/**
 * ThumbnailUtil.ts
 *
 * Client-side thumbnail generation using Canvas API
 * Stores thumbnails in localStorage for fast chat list display
 */

export class ThumbnailUtil {
  private static readonly MAX_DIMENSION = 300;
  private static readonly QUALITY = 0.8;

  /**
   * Generate thumbnail from image file using Canvas API
   * Returns base64 data URL (JPEG format)
   */
  static async generateThumbnail(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('[ThumbnailUtil] Failed to get canvas context');
        resolve(null);
        return;
      }

      img.onload = () => {
        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > this.MAX_DIMENSION) {
            height = (height * this.MAX_DIMENSION) / width;
            width = this.MAX_DIMENSION;
          }
        } else {
          if (height > this.MAX_DIMENSION) {
            width = (width * this.MAX_DIMENSION) / height;
            height = this.MAX_DIMENSION;
          }
        }

        // Set canvas size and draw resized image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 JPEG
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', this.QUALITY);
        resolve(thumbnailDataUrl);

        // Cleanup
        URL.revokeObjectURL(img.src);
      };

      img.onerror = () => {
        console.error('[ThumbnailUtil] Failed to load image');
        URL.revokeObjectURL(img.src);
        resolve(null);
      };

      // Load image from file
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Store thumbnail in localStorage
   * Key format: 'thumb_{messageId}'
   */
  static storeThumbnail(messageId: string, thumbnailDataUrl: string): void {
    try {
      const key = `thumb_${messageId}`;
      localStorage.setItem(key, thumbnailDataUrl);
    } catch (error) {
      console.warn('[ThumbnailUtil] localStorage full, thumbnail not saved', error);
      // Non-blocking: continue without thumbnail
    }
  }

  /**
   * Retrieve thumbnail from localStorage
   * Returns data URL or null if not found
   */
  static getThumbnail(messageId: string): string | null {
    try {
      const key = `thumb_${messageId}`;
      return localStorage.getItem(key);
    } catch (error) {
      console.error('[ThumbnailUtil] Failed to retrieve thumbnail', error);
      return null;
    }
  }

  /**
   * Clean up old thumbnails (optional - call periodically)
   * Removes thumbnails for messages not in current room
   */
  static cleanupOldThumbnails(currentMessageIds: string[]): void {
    try {
      const thumbKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('thumb_')) {
          thumbKeys.push(key);
        }
      }

      thumbKeys.forEach(key => {
        const messageId = key.replace('thumb_', '');
        if (!currentMessageIds.includes(messageId)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[ThumbnailUtil] Cleanup failed', error);
    }
  }
}
